/**
 * Live Stream studio (v2, native build) — every decision, figure and string the
 * screen shows. The .tsx files under `pages/live-studio/` are markup and wiring only.
 *
 * WHY: vitest here collects `client/src/**\/__tests__/**\/*.test.ts` — never `.tsx`.
 * Anything that lives in a component is UNTESTED, so every rule that can be a pure
 * function is one here, held by `../__tests__/liveStudio.test.ts`.
 *
 * SELF-CONTAINED ON PURPOSE. An earlier v2 attempt lives in `./liveStream.ts`; this
 * module does not import it, so the controller can retire that build without this
 * one breaking.
 *
 * NOTHING HERE FETCHES. Callers pass rows in; rows come out.
 *
 * The classic screen (`client/src/pages/LiveStreamPortal.tsx`) is the behaviour
 * reference. Where this file departs from it, the function says so and why, and the
 * departure is listed in the build report.
 */

/* ──────────────────────────── shapes (the API's, unchanged) ──────────────────────────── */

export interface LiveStreamRow {
  id: number;
  slug: string;
  weddingId: number;
  coupleNamesOverride: string | null;
  coverImageKey: string | null;
  welcomeMessage: string | null;
  schedule: unknown;
  themeColor: string | null;
  theme: string | null;
  scheduledStart: string | null;
  status: string;
  expiresAt: string | null;
  renewedUntil: string | null;
  requiresPassword: boolean;
  subdomain: string | null;
  isPublic: boolean;
  setupComplete?: boolean;
  destinations?: Destination[];
  /** REPLAY only: 'ready' | 'building' | 'unavailable' (audit 2026-09-27 M5). Absent on older servers. */
  replayPhase?: "ready" | "building" | "unavailable" | null;
}

export interface Destination {
  id: number;
  platform: string;
  ingestUrl: string;
  enabled: boolean;
  status: string;
  lastError: string | null;
}

export interface GuestMessage {
  id: number;
  name: string | null;
  message: string;
  approved: boolean;
  createdAt: string;
}

export interface StreamStats {
  totalViews: number;
  uniqueViewers: number;
  chatMessages: number;
  wishes: { total: number; approved: number; pending: number };
  liveViewers: number;
}

export interface ScheduleItem { time: string; label: string }

export interface DomainInfo {
  customDomain: string | null;
  tld: string | null;
  type: string | null;
  status: null | "payment_pending" | "purchasing" | "dns_pending" | "active" | "failed";
  requestedAt: string | null;
  activatedAt: string | null;
}

export interface DomainOption { domain: string; tld: string; available: boolean; price: number }

export interface KeepsakePlan { id: string; months: number; amount: number; price: string; label: string }

export interface KeepsakeInfo {
  expiresAt: string | null;
  renewedUntil: string | null;
  effectiveExpiry: string | null;
  daysLeft: number | null;
  state: "free" | "expiring" | "expired" | "none";
  coupleNames: string | null;
  plans: KeepsakePlan[];
}

export type Tone = "gold" | "muted" | "alert";
export interface Badge { label: string; tone: Tone }

/** His badge class for a tone. `.badge` is gold by default; ember is `.alert`. */
export function badgeClass(tone: Tone): string {
  return tone === "muted" ? "badge muted" : tone === "alert" ? "badge alert" : "badge";
}

/* ──────────────────────────── dates, pinned to IST ──────────────────────────── */

/*
 * Every date is resolved through Intl in Asia/Kolkata and never through a bare
 * `toLocale*()`: the classic hero calls `toLocaleDateString(undefined, …)`, which
 * renders in the VIEWER's zone, so a ceremony at 00:30 IST reads as the previous day
 * west of IST. The portal also monkey-patches Date.prototype to IST, but a test
 * environment does not, so this file never relies on it.
 *
 * Month names are spelled out, not asked of `month:"short"`: from ICU 72 `en-GB`
 * and `en-IN` abbreviate September as "Sept"; the prototype writes "Sep".
 */
const IST = "Asia/Kolkata";
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"] as const;
const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;
const WEEKDAYS: Record<string, string> = { Sun: "Sunday", Mon: "Monday", Tue: "Tuesday", Wed: "Wednesday", Thu: "Thursday", Fri: "Friday", Sat: "Saturday" };

interface IstParts { weekday: string; day: number; month: number; year: number; hour: number; minute: number }

function toDate(v: string | Date | null | undefined): Date | null {
  if (v === null || v === undefined || v === "") return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function istParts(d: Date): IstParts | null {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: IST, weekday: "short", day: "numeric", month: "numeric", year: "numeric",
    hour: "numeric", minute: "numeric", hourCycle: "h23",
  }).formatToParts(d);
  const pick = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  const n = (t: string) => Number(pick(t));
  const out = { weekday: WEEKDAYS[pick("weekday")] ?? "", day: n("day"), month: n("month"), year: n("year"), hour: n("hour") % 24, minute: n("minute") };
  if (![out.day, out.month, out.year, out.hour, out.minute].every(Number.isFinite) || !out.weekday) return null;
  return out;
}

function clock(p: IstParts): string {
  const h = p.hour % 12 === 0 ? 12 : p.hour % 12;
  return `${h}:${String(p.minute).padStart(2, "0")} ${p.hour < 12 ? "am" : "pm"}`;
}

/** "Saturday, 12 September 2026". Empty for a missing or bad date. */
export function formatLongDate(v: string | Date | null | undefined): string {
  const d = toDate(v); const p = d && istParts(d);
  return p ? `${p.weekday}, ${p.day} ${MONTHS[p.month - 1]} ${p.year}` : "";
}

/** "9 Jul 2027". */
export function formatDayMonthYear(v: string | Date | null | undefined): string {
  const d = toDate(v); const p = d && istParts(d);
  return p ? `${p.day} ${MONTHS_SHORT[p.month - 1]} ${p.year}` : "";
}

/** "12 Sep, 6:30 pm" — a wish's timestamp. */
export function formatDayTime(v: string | Date | null | undefined): string {
  const d = toDate(v); const p = d && istParts(d);
  return p ? `${p.day} ${MONTHS_SHORT[p.month - 1]}, ${clock(p)}` : "";
}

/** "Saturday 12 Sep, 6:30 pm" — the scheduled flag. */
export function formatWeekdayDayTime(v: string | Date | null | undefined): string {
  const d = toDate(v); const p = d && istParts(d);
  return p ? `${p.weekday} ${p.day} ${MONTHS_SHORT[p.month - 1]}, ${clock(p)}` : "";
}

/** "12 Sep" — the journey's short line. */
export function formatDayMonth(v: string | Date | null | undefined): string {
  const d = toDate(v); const p = d && istParts(d);
  return p ? `${p.day} ${MONTHS_SHORT[p.month - 1]}` : "";
}

/** Whole days from `now` until `until` (floor, never negative). null on a bad date. */
export function daysUntil(until: string | Date | null | undefined, now: Date = new Date()): number | null {
  const d = toDate(until);
  if (!d) return null;
  return Math.max(0, Math.floor((d.getTime() - now.getTime()) / 86400000));
}

/* ──────────────────────────── the stream itself ──────────────────────────── */

/** `stream.schedule` may be null, an array, or a JSON string. Exactly the classic's rule. */
export function normalizeSchedule(raw: unknown): ScheduleItem[] {
  if (!raw) return [];
  let v: unknown = raw;
  if (typeof v === "string") {
    try { v = JSON.parse(v); } catch { return []; }
  }
  if (!Array.isArray(v)) return [];
  return v
    .filter((x): x is Record<string, unknown> => !!x && typeof x === "object")
    .map((x) => ({ time: String(x.time ?? ""), label: String(x.label ?? "") }))
    .slice(0, 50);
}

/**
 * The names guests see on the watch page. Sentence case ("Your wedding") per his
 * writing rule; the classic wrote "Your Wedding". The classic hero shows these names
 * and the earlier v2 build dropped them — they are back on the stage tile.
 */
export function coupleDisplayName(stream: Pick<LiveStreamRow, "coupleNamesOverride">): string {
  return stream.coupleNamesOverride?.trim() || "Your wedding";
}

export function watchUrlFor(stream: Pick<LiveStreamRow, "subdomain" | "slug">): string {
  return stream.subdomain ? `https://${stream.subdomain}.lamirage.live` : `https://lamirage.live/w/${stream.slug}`;
}

export function watchHostFor(stream: Pick<LiveStreamRow, "subdomain" | "slug">): string {
  return watchUrlFor(stream).replace(/^https:\/\//, "");
}

/**
 * The cover as the browser can show it. The classic's rule: a value starting with
 * "http" or "/" is a URL; anything else is a bare R2 key the browser cannot fetch.
 */
export function coverKind(key: string | null | undefined): { kind: "url" | "key" | "none"; url: string | null } {
  const v = key ?? "";
  if (!v) return { kind: "none", url: null };
  if (v.startsWith("http") || v.startsWith("/")) return { kind: "url", url: v };
  return { kind: "key", url: null };
}

/** The page head's `.sub`: the classic eyebrow plus the IST-pinned scheduled date. */
export function pageSub(scheduledStart: string | null | undefined): string {
  const date = formatLongDate(scheduledStart);
  return date ? `LA Mirage Live Studio · ${date}` : "LA Mirage Live Studio";
}

/* ──────────────────────────── choosing a stream ──────────────────────────── */

/** His `.chips` picker only exists when there is more than one stream. */
export function streamChips(streams: Array<Pick<LiveStreamRow, "id" | "slug" | "coupleNamesOverride">>): Array<{ id: number; label: string }> {
  if (streams.length < 2) return [];
  return streams.map((s) => ({ id: s.id, label: s.coupleNamesOverride?.trim() || s.slug }));
}

/** Which stream to show: the remembered one if it still exists, else the first. */
export function pickStream<T extends { id: number }>(streams: T[], rememberedId: number | null | undefined): T | null {
  if (streams.length === 0) return null;
  return streams.find((s) => s.id === rememberedId) ?? streams[0];
}

/** Session key so /live-stream/presentation edits the stream the overview had picked. */
export const STREAM_PICK_KEY = "lm:v2:live-studio-stream";

/** A one-shot toast carried across a navigation (setup → studio). */
export const FLASH_KEY = "lm:v2:live-studio-flash";

/* ──────────────────────────── status ──────────────────────────── */

export const JOURNEY_STEPS = ["Setup", "Scheduled", "Live", "Replay"] as const;

/** `--at` for his `.journey`. DRAFT 0, SCHEDULED 1, LIVE 2, REPLAY/EXPIRED 3; unknown → 0 (the classic's rule). */
export function statusStepIndex(status: string): number {
  switch (status) {
    case "DRAFT": return 0;
    case "SCHEDULED": return 1;
    case "LIVE": return 2;
    case "REPLAY":
    case "EXPIRED": return 3;
    default: return 0;
  }
}

export interface JourneyKnot { label: string; sub: string; state: "past" | "now" | "ahead" }

/** Four knots on his gold thread; each carries a short second line so "Scheduled" says when. */
export function buildJourney(stream: Pick<LiveStreamRow, "status" | "scheduledStart">): JourneyKnot[] {
  const at = statusStepIndex(stream.status);
  const subs = [
    at > 0 ? "Done" : "Now",
    stream.scheduledStart ? formatDayMonth(stream.scheduledStart) : "Date to come",
    stream.status === "LIVE" ? "Now" : at > 2 ? "Done" : "On the day",
    stream.status === "EXPIRED" ? "Closed" : at === 3 ? "Ready" : "After",
  ];
  return JOURNEY_STEPS.map((label, i) => ({ label, sub: subs[i], state: i < at ? "past" : i === at ? "now" : "ahead" }));
}

export interface StageFlag { tone: "live" | "idle"; text: string }

/**
 * The dot-led `.flag` on the stage tile.
 * ⛔ GOLD, NOT RED: the classic pulses red, but ember is his errors-only colour, so a
 * red pulse on a healthy stream reads as a fault. `tone:"live"` pulses gold.
 */
export function stageFlag(stream: Pick<LiveStreamRow, "status" | "scheduledStart" | "replayPhase">): StageFlag {
  switch (stream.status) {
    case "LIVE": return { tone: "live", text: "Live now" };
    case "REPLAY":
      // ⛔ "Replay ready" only when the film exists (M5): stream 64 said "ready" for ~30h unbuilt,
      // and the two lost-footage weddings said it forever.
      if (stream.replayPhase === "building") return { tone: "idle", text: "Preparing your replay" };
      if (stream.replayPhase === "unavailable") return { tone: "idle", text: "Recording unavailable" };
      return { tone: "idle", text: "Replay ready" };
    case "EXPIRED": return { tone: "idle", text: "Replay window closed" };
    case "SCHEDULED":
      return { tone: "idle", text: stream.scheduledStart ? `Scheduled · ${formatWeekdayDayTime(stream.scheduledStart)}` : "Scheduled" };
    default: return { tone: "idle", text: "Setting up" };
  }
}

/** The short state `.badge` in the desktop page head. */
export function stateBadge(status: string, phase?: LiveStreamRow["replayPhase"]): Badge {
  switch (status) {
    case "LIVE": return { label: "Live now", tone: "gold" };
    case "SCHEDULED": return { label: "Scheduled", tone: "gold" };
    case "REPLAY":
      if (phase === "building") return { label: "Preparing replay", tone: "muted" };
      if (phase === "unavailable") return { label: "Recording unavailable", tone: "muted" };
      return { label: "Replay ready", tone: "gold" };
    case "EXPIRED": return { label: "Replay closed", tone: "muted" };
    default: return { label: "Setting up", tone: "muted" };
  }
}

/** "Replay available until 9 Jul 2027" — REPLAY/EXPIRED only, renewedUntil before expiresAt. */
export function replayHint(stream: Pick<LiveStreamRow, "status" | "renewedUntil" | "expiresAt" | "replayPhase">): string | null {
  if (stream.status !== "REPLAY" && stream.status !== "EXPIRED") return null;
  // No "available until" for a film that does not exist yet / at all (M5).
  if (stream.status === "REPLAY" && stream.replayPhase && stream.replayPhase !== "ready") return null;
  const d = formatDayMonthYear(stream.renewedUntil ?? stream.expiresAt);
  return d ? `Replay available until ${d}` : null;
}

/* ──────────────────────────── figures ──────────────────────────── */

/** 1284 → "1,284". Indian grouping, as the rest of the portal. */
export function formatCount(n: number | null | undefined): string {
  const v = Number(n);
  if (n === null || n === undefined || !Number.isFinite(v)) return "—";
  return Math.round(v).toLocaleString("en-IN");
}

/**
 * The hero figure. The classic paints `stats?.liveViewers ?? 0`, so a stats call that
 * never answered reads as a confident "0 watching" during a ceremony. Until the call
 * answers — or when it fails — this says "—" instead.
 */
export function heroFigure(stats: StreamStats | undefined): string {
  return stats ? formatCount(stats.liveViewers) : "—";
}

export interface LedgerLine { label: string; value: string; badge: string | null }

/** His `.sum-lines`: the classic's four secondary stat cards, same labels, same badge text. */
export function ledgerLines(stats: StreamStats | undefined): LedgerLine[] {
  const pending = stats?.wishes?.pending ?? 0;
  const v = (n: number | undefined) => (stats ? formatCount(n ?? 0) : "—");
  return [
    { label: "Total views", value: v(stats?.totalViews), badge: null },
    { label: "Unique guests", value: v(stats?.uniqueViewers), badge: null },
    { label: "Wishes", value: v(stats?.wishes?.total), badge: pending > 0 ? `${pending} awaiting` : null },
    { label: "Chat messages", value: v(stats?.chatMessages), badge: null },
  ];
}

/** The classic's cadence: every 10s while LIVE, every 30s otherwise. */
export function statsPollMs(status: string): number {
  return status === "LIVE" ? 10000 : 30000;
}

/* ──────────────────────────── watch button ──────────────────────────── */

/**
 * The stage tile's "Watch as your guests do". With no subdomain the classic DISABLES
 * its button and explains itself in a `title=` tooltip — the one affordance a phone
 * cannot show. Here the button carries its own reason and opens the Address sheet.
 */
export function watchAction(stream: Pick<LiveStreamRow, "subdomain" | "slug">):
  { kind: "watch"; label: string; href: string } | { kind: "address"; label: string } {
  return stream.subdomain
    ? { kind: "watch", label: "Watch as your guests do", href: watchUrlFor(stream) }
    : { kind: "address", label: "Choose your web address first" };
}

/* ──────────────────────────── invite ──────────────────────────── */

export interface AddrBar { title: string; small: string; action: string; primary: boolean; set: boolean }

/** His `.addr` bar. While unset, "Choose" is the view's single `.btn-primary`. */
export function addrBar(stream: Pick<LiveStreamRow, "subdomain">): AddrBar {
  return stream.subdomain
    ? { title: `${stream.subdomain}.lamirage.live`, small: "Your guests' link", action: "Change", primary: false, set: true }
    : { title: "Not set yet", small: "Choose the address guests will use", action: "Choose", primary: true, set: false };
}

/** The classic's invite text, verbatim. */
export function inviteText(names: string, watchUrl: string): string {
  return `Dear friends & family — ${names} are getting married! 💛 Join us and watch the celebration live at LA Mirage: ${watchUrl}`;
}

export function whatsappShareHref(names: string, watchUrl: string): string {
  return `https://wa.me/?text=${encodeURIComponent(inviteText(names, watchUrl))}`;
}

/** The classic's QR filename. */
export function qrFileName(stream: Pick<LiveStreamRow, "subdomain" | "slug">): string {
  return `${stream.subdomain || stream.slug}-live-qr.png`;
}

/** The classic's QR raster: 640px square, 48px white padding. */
export const QR_PNG = { size: 640, pad: 48, background: "#ffffff" } as const;

/** Per stream, per device — the classic's localStorage key, unchanged. */
export const sharedKey = (id: number) => `lm_live_shared_${id}`;

/** How long the copy icon shows its check. */
export const COPY_CHECK_MS = 1500;

/* ──────────────────────────── theme names ──────────────────────────── */

/** Mirrors `@/lib/liveTheme` LIVE_THEMES (ids are the server's whitelist). */
export const THEMES = [
  { id: "midnight", name: "Midnight Gold", swatch: ["#0a0a0a", "#c9a962"] },
  { id: "ivory", name: "Ivory & Blush", swatch: ["#faf6f0", "#b76e79"] },
  { id: "emerald", name: "Emerald & Gold", swatch: ["#08201a", "#d9b45b"] },
  { id: "crimson", name: "Crimson Silk", swatch: ["#1c060a", "#e0aa3e"] },
] as const;

export function themeName(id: string | null | undefined): string {
  return THEMES.find((t) => t.id === (id ?? "midnight"))?.name ?? (id || "Midnight Gold");
}

/* ──────────────────────────── before you go live ──────────────────────────── */

export type ChecklistTarget = "address" | "presentation" | "share";

export interface ChecklistRow {
  id: "address" | "cover" | "theme" | "welcome" | "schedule" | "shared";
  label: string;
  meta: string;
  done: boolean;
  target: ChecklistTarget;
  /** Presentation section to scroll to. */
  section?: PresentationSectionId;
}

/**
 * The six rows, in the classic's order, with the classic's done rules.
 *
 * ⚠️ THEME: the classic computes `!!(stream.theme ?? "midnight")`, which is true for
 * any stream the API returns (the server defaults theme to "midnight"). Reproduced
 * exactly — it still counts in the percentage — but the meta line names the theme so
 * the tick says something true.
 */
export function goLiveChecklist(
  stream: Pick<LiveStreamRow, "subdomain" | "coverImageKey" | "theme" | "welcomeMessage" | "schedule">,
  shared: boolean,
): ChecklistRow[] {
  const moments = normalizeSchedule(stream.schedule).length;
  return [
    { id: "address", label: "Web address", meta: stream.subdomain ? `${stream.subdomain}.lamirage.live` : "Not chosen yet", done: !!stream.subdomain, target: "address" },
    { id: "cover", label: "Cover photo", meta: stream.coverImageKey ? "Added" : "Shown behind your names", done: !!stream.coverImageKey, target: "presentation", section: "cover" },
    { id: "theme", label: "Theme", meta: themeName(stream.theme), done: !!(stream.theme ?? "midnight"), target: "presentation", section: "look" },
    { id: "welcome", label: "Welcome message", meta: stream.welcomeMessage?.trim() ? "Written" : "A note before you go live", done: !!stream.welcomeMessage?.trim(), target: "presentation", section: "welcome" },
    { id: "schedule", label: "Order of events", meta: moments ? `${moments} ${moments === 1 ? "moment" : "moments"}` : "None added yet", done: moments > 0, target: "presentation", section: "order" },
    { id: "shared", label: "Shared with guests", meta: shared ? "Marked on this device" : "Send the link or the QR", done: shared, target: "share" },
  ];
}

/** The classic's percentage (`Math.round(done / 6 * 100)`) and his "4 of 6 ready". */
export function checklistProgress(rows: ChecklistRow[]): { done: number; total: number; pct: number; text: string } {
  const done = rows.filter((r) => r.done).length;
  const total = rows.length;
  return { done, total, pct: total ? Math.round((done / total) * 100) : 0, text: `${done} of ${total} ready` };
}

/** His ring geometry (r=42 on a 100 box). Returns the stroke-dasharray for a share 0..1. */
export const RING_C = 2 * Math.PI * 42;
export function ringDash(share: number): string {
  const s = Math.max(0, Math.min(1, Number.isFinite(share) ? share : 0));
  return `${(s * RING_C).toFixed(2)}, ${RING_C.toFixed(2)}`;
}

/* ──────────────────────────── the web address ──────────────────────────── */

export const SUBDOMAIN = { min: 3, max: 40, suffix: ".lamirage.live" } as const;

/** What the classic field lets through as you type. */
export function sanitizeSubdomain(raw: string): string {
  return raw.toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, SUBDOMAIN.max);
}

export type AvailabilityState = "idle" | "checking" | "available" | "taken" | "invalid";

export interface AvailabilityLine { tone: "hint" | "ok" | "err"; text: string }

/** The line under the address input — the classic's four states plus "current". */
export function availabilityLine(state: AvailabilityState, value: string, current: string | null): AvailabilityLine | null {
  const v = value.trim().toLowerCase();
  const changed = v !== (current ?? "");
  if (state === "checking") return { tone: "hint", text: "Checking…" };
  if (state === "available" && changed) return { tone: "ok", text: "Available" };
  if (state === "taken") return { tone: "err", text: "Already taken" };
  if (state === "invalid") return { tone: "err", text: "Invalid format" };
  if (state === "idle" && !changed && v) return { tone: "ok", text: "This is your current link" };
  return null;
}

/** The classic SubdomainCard rule, exactly: changed, ≥3, and not taken/invalid/checking. */
export function canSaveSubdomain(value: string, current: string | null, state: AvailabilityState): boolean {
  const v = value.trim().toLowerCase();
  return v !== (current ?? "") && v.length >= SUBDOMAIN.min && state !== "taken" && state !== "invalid" && state !== "checking";
}

/** The first-run wizard's step-1 gate, exactly: ≥3 and (available or idle = unchanged). */
export function setupAddressOk(value: string, state: AvailabilityState): boolean {
  return value.trim().length >= SUBDOMAIN.min && (state === "available" || state === "idle");
}

export function subdomainPreview(value: string): string | null {
  const v = value.trim().toLowerCase();
  return v ? `https://${v}${SUBDOMAIN.suffix}` : null;
}

/** A failed address save, in words, under the field it belongs to. */
export function addressSaveError(status: number | undefined): string {
  if (status === 409) return "Already taken — someone chose it while you were typing. Try another.";
  if (status === 400) return "Invalid format. Use 3 to 40 lowercase letters, numbers and hyphens.";
  return "We couldn't save the address. Check your connection and try again.";
}

/* ──────────────────────────── presentation form ──────────────────────────── */

export const NAMES_MAX = 200;
export const WELCOME_MAX = 2000;
export const SCHEDULE_MAX = 50;
export const PASSWORD_MIN = 6;

/** The cover guard's own numbers (the classic's help text said 5 MB; its guard is 10 MB). */
export const COVER = { maxBytes: 10 * 1024 * 1024, accept: "image/*", hint: "JPG, PNG or WebP, up to 10 MB" } as const;

export function coverProblem(file: { type: string; size: number }): string | null {
  if (!file.type.startsWith("image/")) return "Choose an image file — JPG, PNG or WebP.";
  if (file.size > COVER.maxBytes) return "That photo is over 10 MB. Choose one under 10 MB.";
  return null;
}

/**
 * The server's rule (liveStreamRoutes.ts: `guestPassword.length < 6` → 400). The
 * classic sends anything ≥3, so a 4-character password went to the API, was refused,
 * and the 400 was reported as "Invalid subdomain" under an untouched URL field.
 */
export function passwordProblem(value: string): string | null {
  const v = value.trim();
  if (!v) return null;
  return v.length < PASSWORD_MIN ? `Use at least ${PASSWORD_MIN} characters, or leave it empty.` : null;
}

export const PRESENTATION_SECTIONS = [
  { id: "names", label: "Names" },
  { id: "welcome", label: "Welcome" },
  { id: "cover", label: "Cover" },
  { id: "order", label: "Order" },
  { id: "look", label: "Look" },
  { id: "privacy", label: "Privacy" },
] as const;

export type PresentationSectionId = (typeof PRESENTATION_SECTIONS)[number]["id"];

export interface PresentationForm {
  coupleNamesOverride: string;
  welcomeMessage: string;
  themeColor: string;
  theme: string;
  coverImageKey: string;
  guestPassword: string;
  isPublic: boolean;
  schedule: ScheduleItem[];
}

/** The classic PresentationTab's initial state, field for field. */
export function presentationFormOf(stream: LiveStreamRow): PresentationForm {
  return {
    coupleNamesOverride: stream.coupleNamesOverride ?? "",
    welcomeMessage: stream.welcomeMessage ?? "",
    themeColor: stream.themeColor ?? "#c9a962",
    theme: stream.theme ?? "midnight",
    coverImageKey: stream.coverImageKey ?? "",
    guestPassword: "",
    isPublic: stream.isPublic ?? false,
    schedule: normalizeSchedule(stream.schedule),
  };
}

const sameSchedule = (a: ScheduleItem[], b: ScheduleItem[]) =>
  a.length === b.length && a.every((r, i) => r.time === b[i].time && r.label === b[i].label);

/** Which sections differ from what is saved — drives the actionbar count and the leave guard. */
export function changedSections(form: PresentationForm, saved: PresentationForm): PresentationSectionId[] {
  const out: PresentationSectionId[] = [];
  if (form.coupleNamesOverride !== saved.coupleNamesOverride) out.push("names");
  if (form.welcomeMessage !== saved.welcomeMessage) out.push("welcome");
  if (form.coverImageKey !== saved.coverImageKey) out.push("cover");
  if (!sameSchedule(form.schedule, saved.schedule)) out.push("order");
  if (form.theme !== saved.theme || form.themeColor.toLowerCase() !== saved.themeColor.toLowerCase()) out.push("look");
  if (form.isPublic !== saved.isPublic || form.guestPassword.trim() !== "") out.push("privacy");
  return out;
}

export function unsavedLabel(n: number): string {
  if (n === 0) return "All changes saved";
  return n === 1 ? "1 unsaved change" : `${n} unsaved changes`;
}

/**
 * The PATCH body — the classic's, field for field, MINUS `subdomain`.
 *
 * ⛔ The classic Presentation save sends `subdomain: null` whenever its own field is
 * empty, which can un-set a LIVE address guests already hold. The address now has one
 * editor (the Address sheet), so this body never carries it.
 * The password goes only when it passes the server's rule; `passwordProblem` blocks
 * the save before this is called.
 */
export function presentationPatch(form: PresentationForm): Record<string, unknown> {
  const body: Record<string, unknown> = {
    coupleNamesOverride: form.coupleNamesOverride || null,
    welcomeMessage: form.welcomeMessage || null,
    themeColor: form.themeColor,
    theme: form.theme,
    coverImageKey: form.coverImageKey || null,
    isPublic: form.isPublic,
    schedule: form.schedule
      .map((r) => ({ time: r.time.trim(), label: r.label.trim() }))
      .filter((r) => r.time || r.label)
      .slice(0, SCHEDULE_MAX),
  };
  if (form.guestPassword.trim().length >= PASSWORD_MIN) body.guestPassword = form.guestPassword;
  return body;
}

/**
 * Which section a PATCH 400 belongs to, from the server's own message
 * (liveStreamRoutes.ts PATCH /:id names the field in every 400).
 */
export function patchErrorSection(message: string | undefined): PresentationSectionId | "form" {
  const m = (message ?? "").toLowerCase();
  if (m.includes("couplenames")) return "names";
  if (m.includes("welcome")) return "welcome";
  if (m.includes("cover")) return "cover";
  if (m.includes("schedule")) return "order";
  if (m.includes("themecolor") || m.includes("theme")) return "look";
  if (m.includes("password") || m.includes("ispublic")) return "privacy";
  return "form";
}

/** Sections that carry something — the Presentation row's "4 of 6 set" and each section's Done badge. */
export function sectionDone(form: PresentationForm, requiresPassword: boolean): Record<PresentationSectionId, boolean> {
  return {
    names: !!form.coupleNamesOverride.trim(),
    welcome: !!form.welcomeMessage.trim(),
    cover: !!form.coverImageKey.trim(),
    order: form.schedule.some((r) => r.time.trim() || r.label.trim()),
    look: !!form.theme,
    privacy: requiresPassword || form.isPublic,
  };
}

export function sectionsSetCount(form: PresentationForm, requiresPassword: boolean): number {
  return Object.values(sectionDone(form, requiresPassword)).filter(Boolean).length;
}

/** "3 of 50" — his `.count`. */
export function countLabel(n: number, max: number): string {
  return `${n} of ${max}`;
}

/** Accent presets: the four theme accents plus the classic default, in that order. */
export const ACCENTS = [
  { value: "#c9a962", label: "Champagne" },
  { value: "#b76e79", label: "Blush" },
  { value: "#d9b45b", label: "Emerald gold" },
  { value: "#e0aa3e", label: "Crimson gold" },
] as const;

export function isPresetAccent(hex: string): boolean {
  return ACCENTS.some((a) => a.value.toLowerCase() === hex.toLowerCase());
}

/** The server only takes #rrggbb. */
export function isHexColour(v: string): boolean {
  return /^#[0-9a-fA-F]{6}$/.test(v);
}

/* ──────────────────────────── first-run setup ──────────────────────────── */

export const SETUP_STEPS = ["Your link", "Names", "Welcome", "Theme", "Privacy"] as const;

export interface SetupForm {
  subdomain: string;
  coupleNames: string;
  coverImageKey: string;
  welcomeMessage: string;
  theme: string;
  isPublic: boolean;
  guestPassword: string;
}

/** The classic SetupWizard's initial state — note `isPublic ?? true` here, unlike Presentation. */
export function setupFormOf(stream: LiveStreamRow): SetupForm {
  return {
    subdomain: stream.subdomain ?? "",
    coupleNames: stream.coupleNamesOverride ?? "",
    coverImageKey: stream.coverImageKey ?? "",
    welcomeMessage: stream.welcomeMessage ?? "",
    theme: stream.theme ?? "midnight",
    isPublic: stream.isPublic ?? true,
    guestPassword: "",
  };
}

/** The classic `complete()` body, exactly: only changed fields, plus `completeSetup:true`. */
export function setupPatch(form: SetupForm, stream: LiveStreamRow): Record<string, unknown> {
  const body: Record<string, unknown> = { completeSetup: true };
  if (form.subdomain.trim() && form.subdomain.trim() !== (stream.subdomain ?? "")) body.subdomain = form.subdomain.trim().toLowerCase();
  if (form.coupleNames.trim() && form.coupleNames.trim() !== (stream.coupleNamesOverride ?? "")) body.coupleNamesOverride = form.coupleNames.trim();
  if (form.coverImageKey && form.coverImageKey !== (stream.coverImageKey ?? "")) body.coverImageKey = form.coverImageKey;
  if (form.welcomeMessage.trim() && form.welcomeMessage !== (stream.welcomeMessage ?? "")) body.welcomeMessage = form.welcomeMessage;
  if (form.theme && form.theme !== (stream.theme ?? "midnight")) body.theme = form.theme;
  if (form.isPublic !== (stream.isPublic ?? true)) body.isPublic = form.isPublic;
  if (form.guestPassword.trim().length >= PASSWORD_MIN) body.guestPassword = form.guestPassword.trim();
  return body;
}

/** Whole-page routing rule: the classic shows the wizard whenever `setupComplete === false`. */
export function mustRunSetup(stream: Pick<LiveStreamRow, "setupComplete"> | null | undefined): boolean {
  return !!stream && stream.setupComplete === false;
}

/* ──────────────────────────── social destinations ──────────────────────────── */

export type SocialPlatform = "youtube" | "facebook";

export const SOCIAL: Record<SocialPlatform, {
  label: string; ingestUrl: string; link: string; linkLabel: string; blurb: string;
  step1Title: string; step1: string; step2Title: string; step2: string; success: string;
}> = {
  youtube: {
    label: "YouTube",
    ingestUrl: "rtmp://a.rtmp.youtube.com/live2",
    link: "https://studio.youtube.com/",
    linkLabel: "Open YouTube Studio",
    blurb: "Stream to your own YouTube channel so family anywhere can watch.",
    step1Title: "Turn on live streaming — do this the day before",
    step1: "Sign in with the account where the wedding should stream. If this channel has never gone live, YouTube must enable live streaming first — it needs a verified phone number and can take up to 24 hours, so set it up at least a day ahead. Then click Create → Go live → Now.",
    step2Title: "Pick ‘Streaming software’ + turn on Auto-start",
    step2: "Skip webcam — choose Streaming software. In the settings, switch on Auto-start so it goes live by itself on the day. Your Stream key is shown under “Stream settings”.",
    success: "YouTube connected — on the wedding day we push your celebration to your channel automatically, and with Auto-start on it goes live with no clicks. YouTube reuses this stream key, so you're set.",
  },
  facebook: {
    label: "Facebook",
    ingestUrl: "rtmps://live-api-s.facebook.com:443/rtmp/",
    link: "https://www.facebook.com/live/producer?ref=lamirage",
    linkLabel: "Open Facebook Live",
    blurb: "Go live on your Facebook profile or page for friends to join in.",
    step1Title: "Open Live Producer → Streaming software",
    step1: "On facebook.com open Live Producer and choose Streaming software.",
    step2Title: "Turn on a Persistent Stream Key, then copy it",
    step2: "Below the preview, open “Use Stream Key” and tick “Use a persistent stream key” — otherwise Facebook’s key expires before the wedding. Then Copy the Stream key.",
    success: "Facebook connected — we push your celebration to Facebook automatically on the day. Because Facebook needs a broadcast to be started, either schedule your Live with “Go live automatically”, or just tap Go Live on your post when we message you it's streaming.",
  },
};

export const SOCIAL_ORDER: SocialPlatform[] = ["youtube", "facebook"];

export function platformOf(d: Pick<Destination, "platform">): SocialPlatform {
  return d.platform === "facebook" ? "facebook" : "youtube";
}

/** The classic's soft, non-blocking key check — shown as `.hint`, never `.err`. */
export function socialKeyWarning(platform: SocialPlatform, key: string): string | null {
  const k = key.trim();
  if (!k) return null;
  if (/^(https?|rtmps?):\/\//i.test(k) || k.includes("://")) return "That looks like a URL — paste the Stream key itself, not the stream URL.";
  if (platform === "youtube") {
    return /^[a-z0-9]{4}(-[a-z0-9]{4}){3,5}$/i.test(k) ? null : "This doesn't look like a YouTube stream key — double-check you copied the Stream key, not the URL.";
  }
  return /\s/.test(k) ? "Stream keys never contain spaces — double-check you copied the whole key and nothing extra." : null;
}

/** One platform's state badge in the Social sheet. */
export function destinationBadge(d: Destination | undefined): Badge {
  if (!d) return { label: "Not connected", tone: "muted" };
  if (d.lastError || d.status === "error") return { label: "Needs attention", tone: "alert" };
  if (!d.enabled) return { label: "Paused", tone: "muted" };
  if (d.status === "live") return { label: "Live now", tone: "gold" };
  return { label: "Connected", tone: "gold" };
}

/** The switch's always-visible label. */
export function switchLabel(enabled: boolean): string {
  return enabled ? "Streaming on" : "Paused";
}

/** The classic toggle toast, both lines. */
export function toggleToast(platform: SocialPlatform, enabled: boolean): string {
  const l = SOCIAL[platform].label;
  return enabled
    ? `${l} streaming on — we'll stream to this account when your wedding goes live.`
    : `${l} paused — we won't stream to this account until you turn it back on.`;
}

/**
 * Whether the Social sheet knows the couple's channels. ⛔ `undefined` destinations
 * (detail route loading or failed) are UNKNOWN, never "none": treating them as none
 * offered "Connect" and saved with `enabled: true`, silently un-pausing a channel.
 */
export type DestinationsState = "loading" | "failed" | "ready";
export function destinationsState(dests: Destination[] | undefined, failed: boolean): DestinationsState {
  if (dests) return "ready";
  return failed ? "failed" : "loading";
}

/**
 * The PUT /destination body, or null when it must not be sent. A replaced key keeps
 * the channel's current on/paused state; only a genuinely new channel starts on.
 * ⛔ null while destinations are unknown — "no existing row" cannot be told apart
 * from "not loaded", and guessing re-enables a paused channel.
 */
export function destinationSavePayload(platform: SocialPlatform, key: string, dests: Destination[] | undefined):
  { platform: SocialPlatform; ingestUrl: string; streamKey: string; enabled: boolean } | null {
  const k = key.trim();
  if (!dests || !k) return null;
  const existing = dests.find((d) => platformOf(d) === platform);
  return { platform, ingestUrl: SOCIAL[platform].ingestUrl, streamKey: k, enabled: existing ? existing.enabled : true };
}

/** "YouTube on · Facebook paused", or "Not connected". */
export function destinationsSummary(dests: Destination[]): string {
  const parts = SOCIAL_ORDER.flatMap((p) => {
    const d = dests.find((x) => platformOf(x) === p);
    return d ? [`${SOCIAL[p].label} ${d.enabled ? "on" : "paused"}`] : [];
  });
  return parts.length ? parts.join(" · ") : "Not connected";
}

/* ──────────────────────────── custom domain ──────────────────────────── */

export const DOMAIN_PRICE_LABEL = "₹999";
export const DOMAIN_MIN_BASE = 3;
export const DOMAIN_POLL_MS = 15000;

export function sanitizeDomainBase(raw: string): string {
  return raw.toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, 40);
}

/** The classic's three rendered states and the purchase flow's visibility rule. */
export function domainState(info: DomainInfo | undefined): {
  badge: Badge; flag: string | null; canBuy: boolean; polling: boolean;
} {
  const s = info?.status ?? null;
  const name = info?.customDomain ?? "your domain";
  if (s === "active") return { badge: { label: "Active", tone: "gold" }, flag: "Live & secured", canBuy: false, polling: false };
  if (s === "purchasing") return { badge: { label: "Setting up", tone: "gold" }, flag: "Registering your domain…", canBuy: false, polling: true };
  if (s === "dns_pending") return { badge: { label: "Setting up", tone: "gold" }, flag: "Securing HTTPS and pointing it to your live page. This usually takes 5–15 minutes.", canBuy: false, polling: true };
  if (s === "failed") {
    return {
      badge: { label: "Setup stopped", tone: "alert" },
      flag: `Something went wrong while setting up ${name}. Our team has been alerted — please contact us and we'll sort it out (you won't be charged twice).`,
      canBuy: true, polling: false,
    };
  }
  return { badge: { label: "Not bought", tone: "muted" }, flag: null, canBuy: true, polling: false };
}

export function allTakenLine(options: DomainOption[], base: string): string | null {
  return options.length > 0 && options.every((o) => !o.available) ? `None of these are free for "${base}". Try a different name.` : null;
}

/* ──────────────────────────── download ──────────────────────────── */

export const DOWNLOAD_QUALITIES = [
  { quality: "hd" as const, label: "Full HD — 1080p", note: "Best for keeping & sharing — downloads reliably" },
  { quality: "uhd" as const, label: "Ultra HD — 4K", note: "Maximum quality — a very large file, best on a fast connection" },
];

export type DownloadQuality = (typeof DOWNLOAD_QUALITIES)[number]["quality"];

/**
 * GET /api/live-manage/:id/download. Since 2026-10-05 the files are built on Host B and served from
 * Wasabi: while `status` is "generating" the server also sends `phase` (queued | preparing |
 * uploading), `progress` (0–100 within the phase, when known) and `message` — the couple-facing
 * sentence, rendered as-is.
 */
export interface DownloadData {
  status?: string; sizeBytes?: number; reason?: string; error?: string;
  phase?: "queued" | "preparing" | "uploading"; progress?: number; message?: string;
}

export interface DownloadRowView {
  state: "disabled" | "blocked" | "idle" | "generating" | "ready" | "error";
  /** The one 44px action, or null when there is nothing to press. */
  action: "Prepare" | "Download" | "Retry" | null;
  meta: string;
  reason: string | null;
}

/** The classic's MB rounding: `Math.round(bytes / 1048576)`. */
export function sizeMb(bytes: number | null | undefined): string | null {
  const v = Number(bytes);
  return bytes && Number.isFinite(v) && v > 0 ? `${Math.round(v / 1048576)} MB` : null;
}

export const DOWNLOAD_BEFORE_REPLAY = "Ready shortly after your live ends. Come back once the ceremony is over.";

/**
 * One quality row. Before REPLAY the row is disabled and says why (the classic hid
 * the options behind one sentence). After it, a 409 from the server — REPLAY but the
 * video not built yet, or the keepsake window passed — shows the server's own
 * sentence on the row as `.err`.
 */
export function downloadRowView(
  spec: (typeof DOWNLOAD_QUALITIES)[number], streamStatus: string, data: DownloadData | undefined,
): DownloadRowView {
  if (streamStatus !== "REPLAY") return { state: "disabled", action: null, meta: spec.note, reason: DOWNLOAD_BEFORE_REPLAY };
  const size = sizeMb(data?.sizeBytes);
  const meta = size ? `${spec.note} · ${size}` : spec.note;
  if (data?.reason) return { state: "blocked", action: null, meta, reason: data.reason };
  const s = data?.status ?? "idle";
  if (s === "ready") return { state: "ready", action: "Download", meta, reason: null };
  // The server's own sentence: "in the queue…", "Preparing your file — 42%", "Almost ready…".
  if (s === "generating") return { state: "generating", action: null, meta, reason: data?.message ?? null };
  // The server's couple-facing sentence for a failed build (audit 2026-09-27 H5) — never a raw error.
  if (s === "error") return { state: "error", action: "Retry", meta, reason: data?.message ?? data?.error ?? null };
  return { state: "idle", action: "Prepare", meta, reason: null };
}

/** The classic's poll: every 5s while generating. */
export function downloadPollMs(data: DownloadData | undefined): number | false {
  return data?.status === "generating" ? 5000 : false;
}

/**
 * Stands in for a quality whose GET /download failed with anything but a 409 (the
 * 409 is already a `reason`). `undefined` stays "still loading"; this is "we asked
 * and could not find out" — neither may be read as "ready".
 */
export const DOWNLOAD_CHECK_FAILED: DownloadData = { status: "check-failed" };

/** What the list row gets for one quality: the answer, "still loading", or "could not check". */
export function downloadProbe(data: DownloadData | undefined, isError: boolean): DownloadData | undefined {
  return data ?? (isError ? DOWNLOAD_CHECK_FAILED : undefined);
}

/**
 * The Download row on the studio list.
 * ⛔ "Ready" ONLY when a quality is actually downloadable (status "ready"). It used to
 * be the catch-all for every REPLAY stream not generating — including a 409 (replay
 * still being built, or the keepsake window passed), a never-prepared file, and a
 * still-loading or failed check — so the row said Ready while its own sheet said
 * "available to download shortly". Every other state says what it is, neutrally.
 */
export function downloadRowBadge(streamStatus: string, datas: Array<DownloadData | undefined>): Badge {
  if (streamStatus !== "REPLAY") return { label: "After the wedding", tone: "muted" };
  const states = datas.map((d) => (d === undefined ? "unknown" : d.status === DOWNLOAD_CHECK_FAILED.status ? "failed"
    : downloadRowView(DOWNLOAD_QUALITIES[0], "REPLAY", d).state));
  if (states.includes("ready")) return { label: "Ready", tone: "gold" };
  if (states.includes("generating")) return { label: "Preparing", tone: "gold" };
  if (states.includes("error")) return { label: "Try again", tone: "alert" };
  if (states.includes("idle")) return { label: "Not prepared yet", tone: "muted" };
  if (states.length === 0 || states.includes("unknown")) return { label: "Checking…", tone: "muted" };
  if (states.includes("failed")) return { label: "Couldn't check", tone: "muted" };
  return { label: "Not available yet", tone: "muted" };
}

/** The Download row's meta: the server's own sentence when no quality can be had. */
export function downloadRowMeta(streamStatus: string, datas: Array<DownloadData | undefined>): string {
  if (streamStatus === "REPLAY" && datas.length > 0 && datas.every((d) => !!d?.reason)) return datas[0]!.reason!;
  return "Full HD or 4K";
}

/* ──────────────────────────── keepsake ──────────────────────────── */

/**
 * The keepsake state badge, the classic's words. Its amber "expiring" chip becomes
 * his gold `.badge` (amber is not in his palette, and ember is for errors); only
 * "Expired" — the one overdue state — is ember.
 */
export function keepsakeBadge(info: Pick<KeepsakeInfo, "state" | "daysLeft"> | undefined): Badge | null {
  if (!info) return null;
  if (info.state === "free") return { label: "Free · 6 months included", tone: "gold" };
  if (info.state === "expiring") return { label: `Expires in ${info.daysLeft ?? 0} days`, tone: "gold" };
  if (info.state === "expired") return { label: "Expired — renew to restore", tone: "alert" };
  return null;
}

/** The sheet's Rozha headline. */
export function keepsakeHeadline(info: KeepsakeInfo | undefined): string {
  const d = info && info.state !== "none" ? formatDayMonthYear(info.effectiveExpiry) : "";
  return d ? `Your film is kept until ${d}` : "Your wedding film is safely preserved.";
}

export function daysLeftLabel(n: number | null | undefined): string | null {
  if (n === null || n === undefined || !Number.isFinite(n)) return null;
  return n === 1 ? "1 day left" : `${n} days left`;
}

/** The single sticky primary: "Keep for 6 months · ₹499". */
export function keepsakeCta(plan: KeepsakePlan | undefined): string {
  return plan ? `Keep for ${plan.label} · ${plan.price}` : "Choose how long";
}

/**
 * Did the verify call succeed?
 * ⚠️ The server answers a first verify with `{ ok:true, renewedUntil }` but a
 * replayed or raced one with `{ success:true, alreadyProcessed:true }` — no `ok`.
 * The classic checks `verifyBody?.ok` only, so a couple whose payment was already
 * reconciled (e.g. by the webhook first) is told "Payment verification failed".
 */
export function keepsakeVerified(body: unknown): boolean {
  const b = (body ?? {}) as { ok?: unknown; success?: unknown; alreadyProcessed?: unknown };
  return b.ok === true || b.success === true || b.alreadyProcessed === true;
}

export function keepsakePaidToast(until: string | null | undefined, plan: KeepsakePlan): string {
  const d = formatDayMonthYear(until);
  return d ? `Payment received — kept until ${d}` : `Payment received — kept for a further ${plan.label}`;
}

/* ──────────────────────────── the seven studio rows ──────────────────────────── */

export type StudioRowId = "presentation" | "address" | "domain" | "social" | "wishes" | "download" | "keepsake";

export interface StudioRow { id: StudioRowId; label: string; meta: string; badge: Badge; href: string | null }

/**
 * One row per classic tab except Overview (which is this page), IN TAB ORDER.
 * At 390px the classic tab strip is ~760px wide and the two money tabs sit off-screen
 * right; here all seven are on the first screen of the list.
 */
export function studioRows(input: {
  stream: LiveStreamRow;
  stats: StreamStats | undefined;
  destinations: Destination[] | undefined;
  /** The detail route failed (not merely loading) — destinations are unknown. */
  destinationsFailed?: boolean;
  domain: DomainInfo | undefined;
  keepsake: KeepsakeInfo | undefined;
  downloads: Array<DownloadData | undefined>;
  now?: Date;
}): StudioRow[] {
  const { stream, stats, destinations, domain, keepsake, downloads } = input;
  const set = sectionsSetCount(presentationFormOf(stream), stream.requiresPassword);
  const dests = destinations ?? [];
  const anyError = dests.some((d) => !!d.lastError || d.status === "error");
  const pending = stats?.wishes?.pending ?? 0;
  const shown = stats?.wishes?.approved ?? 0;
  const until = keepsake?.effectiveExpiry ?? stream.renewedUntil ?? stream.expiresAt;
  const days = keepsake?.daysLeft ?? daysUntil(until, input.now);
  const kb = keepsakeBadge(keepsake);

  return [
    {
      id: "presentation", label: "Presentation", meta: "Names, welcome, cover, order, look, privacy",
      badge: { label: `${set} of 6 set`, tone: set === 6 ? "gold" : "muted" }, href: "/live-stream/presentation",
    },
    {
      id: "address", label: "Web address",
      meta: stream.subdomain ? `${stream.subdomain}.lamirage.live` : "Not chosen yet",
      badge: stream.subdomain ? { label: "Set", tone: "gold" } : { label: "Not set", tone: "muted" }, href: null,
    },
    {
      id: "domain", label: "Your own domain",
      meta: domain?.customDomain ? `${domain.customDomain} · ${DOMAIN_PRICE_LABEL} one-time` : `${DOMAIN_PRICE_LABEL} one-time`,
      badge: domainState(domain).badge, href: null,
    },
    {
      id: "social", label: "YouTube & Facebook",
      meta: destinations ? destinationsSummary(dests) : input.destinationsFailed ? "We couldn't load this — open to try again" : "Checking…",
      badge: !destinations
        ? (input.destinationsFailed ? { label: "Couldn't check", tone: "muted" } : { label: "Checking…", tone: "muted" })
        : anyError ? { label: "Needs attention", tone: "alert" }
        : dests.length ? { label: "Connected", tone: "gold" } : { label: "Not connected", tone: "muted" },
      href: null,
    },
    {
      id: "wishes", label: "Wishes from guests",
      meta: stats ? `${formatCount(pending)} awaiting · ${formatCount(shown)} shown` : "Guests' messages of love",
      badge: pending > 0 ? { label: `${pending} awaiting`, tone: "gold" } : { label: "All shown", tone: "muted" },
      href: "/live-stream/wishes",
    },
    {
      id: "download", label: "Download the film", meta: downloadRowMeta(stream.status, downloads),
      badge: downloadRowBadge(stream.status, downloads), href: null,
    },
    {
      id: "keepsake", label: "Keep your film",
      meta: until ? [`Until ${formatDayMonthYear(until)}`, daysLeftLabel(days)].filter(Boolean).join(" · ") : "Six months are included",
      badge: kb ?? { label: "Included", tone: "muted" }, href: null,
    },
  ];
}

/* ──────────────────────────── wishes ──────────────────────────── */

export type WishFilter = "all" | "pending" | "approved";

export const WISH_TABS: ReadonlyArray<{ value: WishFilter; label: string }> = [
  { value: "all", label: "All" },
  { value: "pending", label: "Awaiting" },
  { value: "approved", label: "Shown" },
];

export function wishCounts(m: GuestMessage[]): Record<WishFilter, number> {
  const pending = m.filter((x) => !x.approved).length;
  return { all: m.length, pending, approved: m.length - pending };
}

export function filterWishes(m: GuestMessage[], f: WishFilter): GuestMessage[] {
  return f === "pending" ? m.filter((x) => !x.approved) : f === "approved" ? m.filter((x) => x.approved) : m;
}

export function wishName(m: Pick<GuestMessage, "name">): string {
  return m.name || "Anonymous";
}

export function initialsOf(name: string | null | undefined): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  return parts.length ? parts.map((p) => p[0]).join("").slice(0, 2).toUpperCase() : "?";
}

/** The filter's empty line — the classic's words. */
export function wishesEmptyLine(f: WishFilter): string {
  return `No ${f === "pending" ? "awaiting" : "shown"} wishes right now.`;
}

export interface BulkProgress { current: number; total: number; ok: number; failed: number; running: boolean }

/**
 * The bulk-approve button's own label.
 * The classic approves sequentially behind a static "Approving…" and stops at the
 * first failure. This keeps going, counts as it goes, and ends with what happened.
 */
export function bulkLabel(pending: number, p: BulkProgress | null): string {
  if (p?.running) return `Showing ${p.current} of ${p.total}…`;
  if (p && p.failed > 0) return `${p.ok} shown, ${p.failed} failed — try again`;
  return pending === 1 ? "Show the 1 awaiting" : `Show all ${pending} awaiting`;
}

export function bulkToast(ok: number, total: number): string {
  return ok >= total ? `All approved — ${total} ${total === 1 ? "wish is" : "wishes are"} now visible to guests.` : `${ok} of ${total} shown. Try the rest again.`;
}

export const WISHES_POLL_MS = 15000;

/* ──────────────────────────── page state ──────────────────────────── */

export type PageState = "loading" | "provisioning" | "no-wedding" | "failed" | "ready";

/** The classic's `message.includes("no wedding")` test, unchanged. */
export function noWeddingError(message: string | null | undefined): boolean {
  return (message ?? "").includes("no wedding");
}

/**
 * Which whole-page state to render. The classic shows a spinning icon forever when
 * provisioning fails and offers no retry; `failed` here carries a Try again.
 */
export function pageState(i: { isLoading: boolean; loadFailed: boolean; streamCount: number; setupError: string | null }): PageState {
  if (i.isLoading) return "loading";
  if (i.loadFailed) return "failed";
  if (i.streamCount > 0) return "ready";
  if (i.setupError) return noWeddingError(i.setupError) ? "no-wedding" : "failed";
  return "provisioning";
}

/* ──────────────────────────── copy ──────────────────────────── */

export const HELP = {
  lead: "Need a hand?",
  link: "Message us on WhatsApp",
  href: "https://wa.me/919446776666",
  phone: "+91 94467 76666",
} as const;

export const COPY = {
  title: "Live Stream",
  watchingNow: "watching now",
  inviteTitle: "Invite your guests",
  inviteTl: "One link, one QR — guests tap or scan and they're watching.",
  /** The classic invite footnote, restored. */
  printQr: "Print the QR on your invitation cards — guests scan it to join the live stream instantly.",
  youAreInvited: "You are invited",
  sendWhatsApp: "Send on WhatsApp",
  sendWhatsAppHint: "Opens WhatsApp with the invitation written. You choose who gets it.",
  copyLink: "Copy link",
  copied: "Link copied",
  saveQr: "Save QR",
  qrSaved: "QR saved",
  qrFailed: "We couldn't make the QR image. Try again.",
  copyFailed: "Copying isn't allowed here. Press and hold the link to copy it.",
  sharedOnDevice: "Marked as shared on this device",
  checklistTitle: "Before you go live",
  studioTitle: "Your studio",
  editSetup: "Edit setup",
  loadingTitle: "Preparing your live studio…",
  failedTitle: "We couldn't open your live studio",
  failedBody: "The connection dropped on the way. Try again, or message us and we'll set it up for you.",
  noWeddingTitle: "No wedding booking found",
  noWeddingBody: "No wedding booking found for your account. Please contact LA Mirage if you believe this is an error.",
  tryAgain: "Try again",
  messageUs: "Message us",
} as const;
